/**
 * Formulário de registro do prontuário (etapa 3): o documento e o simulado. As recusas repetem as do
 * servidor (§3.3) para a pessoa corrigir junto do campo; a validade do simulado não se pergunta,
 * porque o servidor conta 12 meses a partir da data (§4.2).
 */
import type {
  FlammableRecord,
  FlammableRecordFormData,
  FlammableRecordMethodology,
  FlammableRecordResult,
  FlammableRecordType,
} from '../../../../../types/flammableInstallation';
import { hojeComoTexto } from '../../flammableDates';

export interface RegistroValues {
  /** `YYYY-MM-DD`, como o campo de data devolve. */
  documentedAt: string;
  responsible: string;
  responsibleRegistration: string;
  location: string;
  nextReviewDate: string;
  /** Só na análise de riscos. */
  methodology: FlammableRecordMethodology | '';
  /** Só no simulado; vazio até a pessoa escolher. */
  result: FlammableRecordResult | '';
  observations: string;
}

export type RegistroErrors = Partial<Record<keyof RegistroValues, string>>;

export const REGISTRO_VAZIO: RegistroValues = {
  documentedAt: '',
  responsible: '',
  responsibleRegistration: '',
  location: '',
  nextReviewDate: '',
  methodology: '',
  result: '',
  observations: '',
};

export const valoresDoRegistro = (registro: FlammableRecord): RegistroValues => ({
  documentedAt: registro.documentedAt.slice(0, 10),
  responsible: registro.responsible,
  responsibleRegistration: registro.responsibleRegistration ?? '',
  location: registro.location ?? '',
  nextReviewDate: registro.nextReviewDate?.slice(0, 10) ?? '',
  methodology: registro.methodology ?? '',
  result: registro.result ?? '',
  observations: registro.observations ?? '',
});

/** Nova versão parte de quem elaborou a anterior; as datas e o que mudou a cada revisão ficam em branco. */
export const valoresDaNovaVersao = (anterior: FlammableRecord): RegistroValues => ({
  ...REGISTRO_VAZIO,
  responsible: anterior.responsible,
  responsibleRegistration: anterior.responsibleRegistration ?? '',
  location: anterior.location ?? '',
  methodology: anterior.methodology ?? '',
});

const DATA_ISO = /^\d{4}-\d{2}-\d{2}$/;

export const validarRegistro = (
  tipo: FlammableRecordType,
  values: RegistroValues,
  hoje: string = hojeComoTexto()
): RegistroErrors => {
  const errors: RegistroErrors = {};
  const simulado = tipo === 'SIMULADO';
  const objeto = simulado ? 'do simulado' : 'do documento';

  if (values.documentedAt === '') errors.documentedAt = `Diga a data ${objeto}.`;
  else if (!DATA_ISO.test(values.documentedAt)) errors.documentedAt = `Use uma data que existe para a data ${objeto}.`;
  else if (values.documentedAt > hoje) errors.documentedAt = `A data ${objeto} não pode estar no futuro.`;

  if (values.responsible.trim() === '') {
    errors.responsible = simulado ? 'Diga quem conduziu o simulado.' : 'Diga quem elaborou ou coordenou o documento.';
  }

  if (!simulado && values.nextReviewDate !== '') {
    if (!DATA_ISO.test(values.nextReviewDate)) errors.nextReviewDate = 'Use uma data que existe para a próxima revisão.';
    else if (DATA_ISO.test(values.documentedAt) && values.nextReviewDate < values.documentedAt) {
      errors.nextReviewDate = 'A próxima revisão não pode ser antes da data do documento.';
    }
  }

  if (tipo === 'ANALISE_RISCOS' && values.methodology === '') errors.methodology = 'Escolha a metodologia da análise.';
  if (simulado && values.result === '') errors.result = 'Escolha o resultado do simulado.';

  return errors;
};

const textoOuNulo = (texto: string): string | null => (texto.trim() === '' ? null : texto.trim());

/**
 * O que cada tipo aceita: o simulado leva resultado e nunca próxima revisão nem registro no
 * conselho; só a análise leva metodologia. O servidor recusa o que sobra.
 */
export const registroParaEnvio = (tipo: FlammableRecordType, values: RegistroValues): FlammableRecordFormData => {
  const base: FlammableRecordFormData = {
    recordType: tipo,
    documentedAt: values.documentedAt,
    responsible: values.responsible.trim(),
    location: textoOuNulo(values.location),
    observations: textoOuNulo(values.observations),
  };
  if (tipo === 'SIMULADO') {
    if (values.result !== '') base.result = values.result;
    return base;
  }
  base.responsibleRegistration = textoOuNulo(values.responsibleRegistration);
  base.nextReviewDate = values.nextReviewDate === '' ? null : values.nextReviewDate;
  if (tipo === 'ANALISE_RISCOS' && values.methodology !== '') base.methodology = values.methodology;
  return base;
};
